import { useEffect, useRef, type RefObject } from "react";

/**
 * El degradado que sigue al ratón.
 *
 * Escribe dos variables CSS —`--hero-x` y `--hero-y`— en el nodo del
 * resplandor. Quien las usa es una clase de Tailwind con el degradado
 * radial; aquí solo viajan dos porcentajes.
 *
 * ── Solo con puntero fino ────────────────────────────────────────────
 * En una pantalla táctil no hay cursor que seguir: el único `pointermove`
 * que llega es el del dedo mientras arrastra, y el resplandor aparecería
 * de golpe bajo el pulgar al desplazarse. `(pointer: fine)` deja el efecto
 * donde tiene sentido y ahorra el listener en el resto.
 *
 * ── El puntero se mueve más rápido que la pantalla ───────────────────
 * `pointermove` dispara mucho más de sesenta veces por segundo. Igual que
 * en el parallax, el evento solo apunta la última posición y el trabajo
 * ocurre una vez por fotograma, escribiendo en el nodo sin pasar por
 * React.
 */
export function useHeroPointerGlow(enabled: boolean): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const glow = ref.current;
    if (!glow || !enabled) return;
    if (
      typeof window.matchMedia === "function" &&
      !window.matchMedia("(pointer: fine)").matches
    ) {
      return;
    }

    let frame = 0;
    let pointerX = 0;
    let pointerY = 0;

    const paint = () => {
      frame = 0;
      // El rectángulo se vuelve a medir en cada fotograma a propósito: el
      // nodo vive DENTRO de la capa del parallax, así que se mueve con el
      // desplazamiento. Guardarlo en caché haría que el resplandor se
      // separase del cursor en cuanto la página bajara.
      const rect = glow.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = ((pointerX - rect.left) / rect.width) * 100;
      const y = ((pointerY - rect.top) / rect.height) * 100;
      glow.style.setProperty("--hero-x", `${x.toFixed(2)}%`);
      glow.style.setProperty("--hero-y", `${y.toFixed(2)}%`);
    };

    const onMove = (event: PointerEvent) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!frame) frame = requestAnimationFrame(paint);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      glow.style.removeProperty("--hero-x");
      glow.style.removeProperty("--hero-y");
    };
  }, [enabled]);

  return ref;
}
